// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { useEffect, useMemo, useState } from 'react';
import { AssessmentReport } from './AssessmentModal';
import {
  PAGE_SIZE,
  formatValue,
  formatScore,
  filterReportsByDateRange,
  filterReportsByEmail,
  exportReportsToExcel
} from './Reportutils';
import {
  paginationBarStyle,
  pageButtonStyle,
  pageButtonDisabledStyle,
  filterBarStyle,
  fieldWrapStyle,
  fieldLabelStyle,
  dateInputStyle,
  searchInputStyle,
  clearFilterButtonStyle,
  exportButtonStyle,
  exportButtonDisabledStyle
} from './Reportstyles';

export interface AllAssessmentsTabProps {
  onViewReport: (report: AssessmentReport) => void;
}

// TODO: point this at the new "all assessments" API once it's built.
const ALL_ASSESSMENTS_ENDPOINT = '/api/assessment/reports/allAssessments';

const AllAssessmentsTab = ({ onViewReport }: AllAssessmentsTabProps): JSX.Element => {
  const [allReports, setAllReports] = useState<AssessmentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchAllReports = async (): Promise<void> => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(ALL_ASSESSMENTS_ENDPOINT);

        if (!response.ok) {
          throw new Error(`Failed to fetch all assessments: ${response.status}`);
        }

        const result = await response.json();
        setAllReports(result.data || []);
      } catch (fetchError) {
        console.error('[AllAssessmentsTab] Error fetching all assessments:', fetchError);
        setError('Unable to load assessments.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllReports();
  }, []);

  const filteredReports = useMemo(() => {
    const byDate = filterReportsByDateRange(allReports, dateFrom, dateTo);
    return filterReportsByEmail(byDate, emailSearch);
  }, [allReports, dateFrom, dateTo, emailSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredReports.length / PAGE_SIZE));

  const pagedReports = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredReports.slice(start, start + PAGE_SIZE);
  }, [filteredReports, page]);

  const isFilterActive = !!(dateFrom || dateTo || emailSearch.trim());

  const handleDateFromChange = (value: string): void => {
    setDateFrom(value);
    setPage(1);
  };

  const handleDateToChange = (value: string): void => {
    setDateTo(value);
    setPage(1);
  };

  const handleEmailSearchChange = (value: string): void => {
    setEmailSearch(value);
    setPage(1);
  };

  const clearFilters = (): void => {
    setDateFrom('');
    setDateTo('');
    setEmailSearch('');
    setPage(1);
  };

  const handleExport = async (): Promise<void> => {
    if (filteredReports.length === 0) return;

    try {
      setIsExporting(true);
      // Exports every filtered row, not just the current page.
      await exportReportsToExcel(filteredReports, 'all-assessments');
    } catch (exportError) {
      console.error('[AllAssessmentsTab] Error exporting to Excel:', exportError);
      setError('Unable to export report data.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>All Assessments</h1>
        <span style={{ color: '#666' }}>
          {filteredReports.length} {filteredReports.length === 1 ? 'result' : 'results'}
          {isFilterActive ? ` of ${allReports.length}` : ''}
        </span>
      </div>

      {!loading && !error && allReports.length > 0 && (
        <div style={filterBarStyle}>
          <div style={fieldWrapStyle}>
            <label style={fieldLabelStyle} htmlFor="all-assessments-date-from">
              From
            </label>
            <input
              id="all-assessments-date-from"
              type="date"
              style={dateInputStyle}
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(event) => handleDateFromChange(event.target.value)}
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={fieldLabelStyle} htmlFor="all-assessments-date-to">
              To
            </label>
            <input
              id="all-assessments-date-to"
              type="date"
              style={dateInputStyle}
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(event) => handleDateToChange(event.target.value)}
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={fieldLabelStyle} htmlFor="all-assessments-email-search">
              Search by email
            </label>
            <input
              id="all-assessments-email-search"
              type="text"
              placeholder="name@example.com"
              style={searchInputStyle}
              value={emailSearch}
              onChange={(event) => handleEmailSearchChange(event.target.value)}
            />
          </div>

          {isFilterActive && (
            <button type="button" style={clearFilterButtonStyle} onClick={clearFilters}>
              Clear filters
            </button>
          )}

          <button
            type="button"
            style={filteredReports.length === 0 || isExporting ? exportButtonDisabledStyle : exportButtonStyle}
            disabled={filteredReports.length === 0 || isExporting}
            onClick={handleExport}
          >
            {isExporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>
      )}

      {loading && (
        <div className="loading-container">
          <p>Loading assessments...</p>
        </div>
      )}

      {error && (
        <div className="error-container">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && allReports.length === 0 && (
        <div className="empty-container">
          <p>No assessments found.</p>
        </div>
      )}

      {!loading && !error && allReports.length > 0 && filteredReports.length === 0 && (
        <div className="empty-container">
          <p>No assessments match the current filters.</p>
        </div>
      )}

      {!loading && !error && filteredReports.length > 0 && (
        <>
          <div className="table-container">
            <table className="reports-table assessment-table">
              <thead>
                <tr>
                  <th>User Name</th>
                  <th>Email</th>
                  <th>Functional Area</th>
                  <th>Expertise Level</th>
                  <th>Score</th>
                  <th>Assessed Level</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {pagedReports.map((report: AssessmentReport) => (
                  <tr key={report.assessment_id}>
                    <td>{formatValue(report.user_name)}</td>
                    <td>{formatValue(report.email_id)}</td>
                    <td>{formatValue(report.functional_area)}</td>
                    <td>{formatValue(report.expertise_level)}</td>
                    <td>{formatScore(report.overall_score)}</td>
                    <td>{formatValue(report.assessed_level)}</td>
                    <td>{formatValue(report.status)}</td>
                    <td>
                      <button type="button" className="view-report-button" onClick={() => onViewReport(report)}>
                        View Report
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredReports.length > PAGE_SIZE && (
            <div style={paginationBarStyle}>
              <span style={{ color: '#666', fontSize: '13px' }}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                style={page === 1 ? pageButtonDisabledStyle : pageButtonStyle}
                disabled={page === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                style={page === totalPages ? pageButtonDisabledStyle : pageButtonStyle}
                disabled={page === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllAssessmentsTab;
